import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { coachService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import CoachInvitationManager from '../components/CoachInvitationManager';
import HomeworkAssignmentModal from '../components/HomeworkAssignmentModal';
import { Badge, Button, Card, Input } from '../components/ui';
import { openMomentumInviteEmail } from '../utils/inviteEmail';

const CoachDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState('participants');
  const [participants, setParticipants] = useState([]);
  const [selectedParticipantId, setSelectedParticipantId] = useState('');
  const [goals, setGoals] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [goalLoading, setGoalLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    pillar: 'mind',
    timeframe: 'weekly',
    targetCount: 3,
    dueDate: ''
  });

  useEffect(() => {
    if (user?.role !== 'coach') {
      navigate('/dashboard');
      return;
    }

    if (!user?.coachAcknowledgement?.acknowledged) {
      navigate('/coach/acknowledge');
      return;
    }

    const loadDashboard = async () => {
      try {
        const [participantsResponse, referralResponse] = await Promise.all([
          coachService.getParticipants(),
          coachService.getReferralCode()
        ]);

        const roster = participantsResponse.data?.data?.participants || [];
        setParticipants(roster);
        setReferralCode(referralResponse.data?.data?.referralCode || '');

        if (roster.length > 0) {
          setSelectedParticipantId(roster[0]._id || roster[0].id);
        }
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load coach dashboard.'
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [navigate, user]);

  useEffect(() => {
    const loadGoals = async () => {
      if (!selectedParticipantId) {
        setGoals([]);
        return;
      }

      setGoalLoading(true);
      try {
        const response = await coachService.getParticipantGoals(selectedParticipantId);
        setGoals(response.data?.data?.goals || []);
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load participant goals.'
        });
      } finally {
        setGoalLoading(false);
      }
    };

    loadGoals();
  }, [selectedParticipantId]);

  const selectedParticipant = useMemo(
    () => participants.find((participant) => (participant._id || participant.id) === selectedParticipantId) || null,
    [participants, selectedParticipantId]
  );

  const totals = useMemo(() => {
    return {
      participants: participants.length,
      activeGoals: goals.filter((goal) => goal.status === 'active').length,
      completedGoals: goals.filter((goal) => goal.status === 'completed').length
    };
  }, [goals, participants]);

  const handleCreateGoal = async () => {
    if (!selectedParticipantId || !newGoal.title.trim()) {
      setMessage({ type: 'error', text: 'Select a participant and enter a goal title.' });
      return;
    }

    try {
      const payload = {
        title: newGoal.title.trim(),
        description: newGoal.description.trim(),
        pillar: newGoal.pillar,
        timeframe: newGoal.timeframe,
        targetCount: Number(newGoal.targetCount) || 1,
        dueDate: newGoal.dueDate || undefined
      };

      const response = await coachService.createGoal(selectedParticipantId, payload);
      setGoals((prev) => [response.data?.data, ...prev]);
      setNewGoal({
        title: '',
        description: '',
        pillar: 'mind',
        timeframe: 'weekly',
        targetCount: 3,
        dueDate: ''
      });
      setMessage({ type: 'success', text: 'Goal created successfully.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to create goal.'
      });
    }
  };

  const handleUpdateGoalProgress = async (goalId, currentCount, status) => {
    try {
      const response = await coachService.updateGoal(goalId, {
        currentCount,
        status
      });
      const updatedGoal = response.data?.data;
      setGoals((prev) => prev.map((goal) => (goal._id === goalId ? updatedGoal : goal)));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update goal.' });
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading coach dashboard..." />;
  }

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Badge variant="secondary" size="sm">Coach Console</Badge>
            <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
              {currentView === 'participants' ? 'Goal Board' : 'Invitations'}
            </h2>
            <p className="mt-2 text-neutral-600">
              {currentView === 'participants'
                ? 'Track engagement-based progress and create participant goals.'
                : 'Send invitations to new participants and track pending invites.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={currentView === 'participants' ? 'primary' : 'secondary'}
              onClick={() => setCurrentView('participants')}
            >
              👥 Participants
            </Button>
            <Button
              variant={currentView === 'invitations' ? 'primary' : 'secondary'}
              onClick={() => setCurrentView('invitations')}
            >
              ✉️ Invitations
            </Button>
          </div>
        </div>

        {currentView === 'invitations' ? (
          <CoachInvitationManager />
        ) : (
          <>
        <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => openMomentumInviteEmail({
                inviterName: `${user.firstName} ${user.lastName}`,
                invitationType: 'coach'
              })}
            >
              Send Invite Email
            </Button>
            <Button variant="primary" onClick={() => setShowHomeworkModal(true)}>Assign Homework</Button>
            <Button variant="primary" onClick={() => navigate('/clinician/messages')}>Send Encouragement</Button>
          </div>

        {message && (
          <div
            className={`mb-6 rounded-soft border p-3 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <p className="text-sm font-medium text-neutral-500">Assigned Participants</p>
            <p className="mt-2 text-2xl font-semibold text-neutral-900">{totals.participants}</p>
          </Card>
          <Card>
            <p className="text-sm font-medium text-neutral-500">Active Goals</p>
            <p className="mt-2 text-2xl font-semibold text-neutral-900">{totals.activeGoals}</p>
          </Card>
          <Card>
            <p className="text-sm font-medium text-neutral-500">Referral Code</p>
            <p className="mt-2 text-xl font-semibold text-neutral-900">{referralCode || 'Generating...'}</p>
          </Card>
        </div>

        <Card className="mt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-neutral-700">Participant</label>
              <select
                className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                value={selectedParticipantId}
                onChange={(event) => setSelectedParticipantId(event.target.value)}
              >
                <option value="">Choose a participant</option>
                {participants.map((participant) => {
                  const id = participant._id || participant.id;
                  return (
                    <option key={id} value={id}>
                      {participant.firstName} {participant.lastName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="rounded-soft border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
              {selectedParticipant
                ? `${selectedParticipant.firstName} ${selectedParticipant.lastName} • Day ${selectedParticipant.currentDay || 1} • Phase ${selectedParticipant.currentPhase || 1}`
                : 'Select a participant to manage goals.'}
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Input
              label="Goal Title"
              value={newGoal.title}
              onChange={(event) => setNewGoal((prev) => ({ ...prev, title: event.target.value }))}
              fullWidth
            />
            <Input
              label="Description (Optional)"
              value={newGoal.description}
              onChange={(event) => setNewGoal((prev) => ({ ...prev, description: event.target.value }))}
              fullWidth
            />
            <div>
              <label className="text-sm font-medium text-neutral-700">Pillar</label>
              <select
                className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                value={newGoal.pillar}
                onChange={(event) => setNewGoal((prev) => ({ ...prev, pillar: event.target.value }))}
              >
                <option value="mind">Mind</option>
                <option value="move">Move</option>
                <option value="journal">Journal</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700">Timeframe</label>
              <select
                className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                value={newGoal.timeframe}
                onChange={(event) => setNewGoal((prev) => ({ ...prev, timeframe: event.target.value }))}
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <Input
              label="Target Count"
              type="number"
              value={newGoal.targetCount}
              onChange={(event) => setNewGoal((prev) => ({ ...prev, targetCount: event.target.value }))}
              fullWidth
            />
            <Input
              label="Due Date (Optional)"
              type="date"
              value={newGoal.dueDate}
              onChange={(event) => setNewGoal((prev) => ({ ...prev, dueDate: event.target.value }))}
              fullWidth
            />
          </div>

          <div className="mt-4">
            <Button variant="secondary" onClick={handleCreateGoal} disabled={!selectedParticipantId}>Create Goal</Button>
          </div>
        </Card>

        <Card className="mt-6">
          <h3 className="text-lg font-display font-semibold text-neutral-900">Current Goals</h3>
          {goalLoading ? (
            <p className="mt-4 text-sm text-neutral-500">Loading goals...</p>
          ) : goals.length === 0 ? (
            <p className="mt-4 text-sm text-neutral-500">No goals yet for this participant.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {goals.map((goal) => (
                <div key={goal._id} className="rounded-soft border border-neutral-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-neutral-900">{goal.title}</p>
                      <p className="text-sm text-neutral-600">{goal.pillar} • {goal.currentCount || 0}/{goal.targetCount || 1} • {goal.status}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUpdateGoalProgress(goal._id, (goal.currentCount || 0) + 1, goal.status)}
                      >
                        +1 Progress
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUpdateGoalProgress(goal._id, goal.currentCount || 0, 'completed')}
                      >
                        Mark Complete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
          </>
        )}
      </main>

      <HomeworkAssignmentModal
        isOpen={showHomeworkModal}
        onClose={() => setShowHomeworkModal(false)}
        modalTitle="Assign Coaching Tools"
        allowedSetIds={['coaching']}
        clients={participants.map((participant) => ({
          _id: participant._id || participant.id,
          firstName: participant.firstName,
          lastName: participant.lastName,
          email: participant.email,
          currentDay: participant.currentDay,
        }))}
        onAssignSuccess={() => setMessage({ type: 'success', text: 'Homework assigned successfully.' })}
      />
    </>
  );
};

export default CoachDashboard;
